import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const BulkActionsBar = ({ 
  selectedCount, 
  onBulkStatusChange, 
  onBulkDelete, 
  onBulkServiceAssign,
  onClearSelection,
  availableServices = []
}) => {
  const [selectedAction, setSelectedAction] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState(null);

  const bulkActions = [
    { value: '', label: 'انتخاب عملیات دسته‌ای' },
    { value: 'status-available', label: 'تغییر وضعیت به "در دسترس"' },
    { value: 'status-unavailable', label: 'تغییر وضعیت به "غیرفعال"' },
    { value: 'assign-services', label: 'تخصیص خدمات' },
    { value: 'delete', label: 'حذف کارمندان انتخاب شده' }
  ];

  const serviceOptions = availableServices?.map(service => ({
    value: service?.id,
    label: service?.name,
    description: `${service?.duration} دقیقه - ${service?.price?.toLocaleString('fa-IR')} تومان`
  }));

  const handleActionSelect = (action) => {
    setSelectedAction(action);
    
    if (action === 'delete') {
      setConfirmationAction({
        type: 'delete',
        title: 'حذف کارمندان',
        message: `آیا مطمئن هستید که می‌خواهید ${selectedCount} کارمند انتخاب شده را حذف کنید؟`,
        confirmText: 'حذف',
        onConfirm: () => {
          onBulkDelete();
          setShowConfirmation(false);
          setSelectedAction('');
        }
      });
      setShowConfirmation(true);
    } else if (action?.startsWith('status-')) {
      const status = action?.replace('status-', '');
      const statusLabel = status === 'available' ? 'در دسترس' : 'غیرفعال';
      
      setConfirmationAction({
        type: 'status',
        title: 'تغییر وضعیت',
        message: `وضعیت ${selectedCount} کارمند انتخاب شده به "${statusLabel}" تغییر خواهد کرد.`,
        confirmText: 'تایید',
        onConfirm: () => {
          onBulkStatusChange(status);
          setShowConfirmation(false);
          setSelectedAction('');
        }
      });
      setShowConfirmation(true);
    }
  };

  const handleServiceAssignment = (services) => {
    if (services?.length > 0) {
      setConfirmationAction({
        type: 'services',
        title: 'تخصیص خدمات',
        message: `${services?.length} خدمت به ${selectedCount} کارمند انتخاب شده اضافه خواهد شد.`,
        confirmText: 'تخصیص',
        onConfirm: () => {
          onBulkServiceAssign(services);
          setShowConfirmation(false);
          setSelectedAction('');
        }
      });
      setShowConfirmation(true);
    }
  };

  if (selectedCount === 0) return null;

  return (
    <>
      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 animate-fade-in">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
              <Icon name="Check" size={16} color="white" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">
                {selectedCount?.toLocaleString('fa-IR')} کارمند انتخاب شده
              </p>
              <p className="text-xs text-muted-foreground">
                عملیات دسته‌ای را انتخاب کنید
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            {selectedAction === 'assign-services' ? (
              <div className="flex items-center gap-2 flex-1 sm:flex-initial">
                <Select
                  options={serviceOptions}
                  value={[]}
                  onChange={handleServiceAssignment}
                  multiple
                  searchable
                  placeholder="انتخاب خدمات"
                  className="min-w-48"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedAction('')}
                  iconName="X"
                  iconSize={16}
                >
                  <span className="sr-only">انصراف</span>
                </Button>
              </div>
            ) : (
              <Select
                options={bulkActions}
                value={selectedAction}
                onChange={handleActionSelect}
                placeholder="انتخاب عملیات"
                className="min-w-48"
              />
            )}

            <Button
              variant="outline"
              onClick={onClearSelection}
              iconName="X"
              iconSize={16}
            >
              لغو انتخاب
            </Button>
          </div>
        </div>
      </div>
      {/* Confirmation Modal */}
      {showConfirmation && confirmationAction && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-modal z-modal flex items-center justify-center p-4">
          <div className="bg-card rounded-lg shadow-elevation-4 w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  confirmationAction?.type === 'delete' 
                    ? 'bg-error/10' :'bg-warning/10'
                }`}>
                  <Icon 
                    name={confirmationAction?.type === 'delete' ? 'AlertTriangle' : 'Info'} 
                    size={20} 
                    className={confirmationAction?.type === 'delete' ? 'text-error' : 'text-warning'}
                  />
                </div>
                <h3 className="text-lg font-semibold text-foreground">
                  {confirmationAction?.title}
                </h3>
              </div>

              <p className="text-sm text-muted-foreground mb-6">
                {confirmationAction?.message}
              </p>

              <div className="flex items-center justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowConfirmation(false);
                    setSelectedAction('');
                  }}
                >
                  انصراف
                </Button>
                <Button
                  variant={confirmationAction?.type === 'delete' ? 'destructive' : 'default'}
                  onClick={confirmationAction?.onConfirm}
                >
                  {confirmationAction?.confirmText}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BulkActionsBar;